import { fetchClient } from "@/api/api";
import type {
	actionOnOrderInput,
	actionOnOrderOutput,
	adminResetPasswordInput,
	adminResetPasswordOutput,
	getAdminDashboardInput,
	getAdminDashboardOutput,
	getAllPaymentsInput,
	getAllPaymentsOutput,
	getCommissionSummaryOutput,
	getSenderOwnersInput,
	getSenderOwnersOutput,
	getStSettingOutput,
	getTicketsCountOutput,
	getTicketsInput,
	getTicketsOutput,
	getUsersFacetsOutput,
	getUsersInput,
	getUsersOutput,
	onboardUserInput,
	onboardUserOutput,
	updateStSettingInput,
	updateStSettingOutput,
	updateTicketInput,
	updateTicketOutput,
	updateUserInput,
	updateUserOutput,
} from "@/api/custom_types";
import { useApiError, useTranslation } from "@/hooks";
import { createFacetSource } from "@/services/facets";
import { invalidateDebtQueries } from "@/services/invalidateDebt";
import { queryClient } from "@/utils/queryClient";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { toast as uiToast } from "@st-marketplace/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ORDER_SERVICE_KEYS } from "../order/keys";
import { ADMIN_SERVICE_KEYS } from "./keys";

/** The user list and its facet counts always go stale together. */
const invalidateAdminUsersQueries = () => {
	queryClient.invalidateQueries({
		queryKey: ADMIN_SERVICE_KEYS.useGetAdminUsers(),
	});
	queryClient.invalidateQueries({
		queryKey: ADMIN_SERVICE_KEYS.useGetAdminUsersFacets(),
	});
};

const useGetAdminUsers: CustomQueryHook<getUsersOutput, getUsersInput> = (
	payload,
	options,
) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetAdminUsers(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/users/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

const useCreateAdminUser: CustomMutationHook<
	onboardUserInput,
	onboardUserOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: ADMIN_SERVICE_KEYS.useCreateAdminUser(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/admin/users/",
				{ body: payload.body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.users.createSuccess"));
			invalidateAdminUsersQueries();
		},
	});
};

const useUpdateAdminUser: CustomMutationHook<
	updateUserInput,
	updateUserOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: ADMIN_SERVICE_KEYS.useUpdateAdminUser(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/admin/users/{user_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.users.updateSuccess"));
			invalidateAdminUsersQueries();
		},
	});
};

const useAdminResetPassword: CustomMutationHook<
	adminResetPasswordInput,
	adminResetPasswordOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: ADMIN_SERVICE_KEYS.useAdminResetPassword(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/admin/reset-password",
				{ body: payload.body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.users.resetPasswordSuccess"));
			invalidateAdminUsersQueries();
		},
	});
};

const useActionOnOrder: CustomMutationHook<
	actionOnOrderInput,
	actionOnOrderOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: ADMIN_SERVICE_KEYS.useActionOnOrder(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/marketplace/admin/orders/{order_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			toast.success(t("admin.orders.actionRefundSuccess"));
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetOrders(),
			});
			queryClient.invalidateQueries({
				queryKey: ORDER_SERVICE_KEYS.useGetOrderById(),
			});
			// A refund gives the buyer their debt back and drops the seller's
			// commission, so both lists and the dashboard have moved.
			invalidateDebtQueries();
			invalidateAdminUsersQueries();
		},
	});
};

const useGetStSetting: CustomQueryHook<getStSettingOutput, undefined> = (
	options,
) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetStSetting(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/st-setting/",
				{ throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

const useUpdateStSetting: CustomMutationHook<
	updateStSettingInput,
	updateStSettingOutput
> = () => {
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: ADMIN_SERVICE_KEYS.useUpdateStSetting(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/admin/st-setting/",
				{ body: payload.body, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			uiToast.success(t("admin.stSettings.updateSuccess"));
			queryClient.invalidateQueries({
				queryKey: ADMIN_SERVICE_KEYS.useGetStSetting(),
			});
		},
	});
};

const ADMIN_USERS_FACET_SOURCE = createFacetSource<getUsersFacetsOutput>({
	path: "/v1/marketplace/admin/users/facets/",
	queryKey: (payload) =>
		ADMIN_SERVICE_KEYS.useGetAdminUsersFacets(payload as any),
});

const useGetAdminDashboard: CustomQueryHook<
	getAdminDashboardOutput,
	getAdminDashboardInput
> = (payload, options) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetAdminDashboard(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/dashboard/",
				{ params: { query: payload.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		refetchInterval: 60_000,
		...options,
	});
};

const useGetTickets: CustomQueryHook<getTicketsOutput, getTicketsInput> = (
	payload,
	options,
) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetTickets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/tickets/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		refetchInterval: 30_000,
		...options,
	});
};

/** Polled by the sidebar so a new ticket shows up on every admin page. */
const useGetTicketsCount: CustomQueryHook<getTicketsCountOutput, undefined> = (
	options,
) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetTicketsCount(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/tickets/count/",
				{ throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		refetchInterval: 30_000,
		...options,
	});
};

const useUpdateTicket: CustomMutationHook<
	updateTicketInput,
	updateTicketOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: ADMIN_SERVICE_KEYS.useUpdateTicket(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/marketplace/admin/tickets/{ticket_id}",
				{
					params: { path: payload.path },
					body: payload.body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onError: toastApiError,
		onSuccess: () => {
			for (const queryKey of [
				ADMIN_SERVICE_KEYS.useGetTickets(),
				ADMIN_SERVICE_KEYS.useGetTicketsCount(),
				ADMIN_SERVICE_KEYS.useGetAdminDashboard(),
			]) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};

const useGetSenderOwners: CustomQueryHook<
	getSenderOwnersOutput,
	getSenderOwnersInput
> = (payload, options) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetSenderOwners(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/sender-owners/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

const useGetCommissionSummary: CustomQueryHook<
	getCommissionSummaryOutput,
	undefined
> = (options) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetCommissionSummary(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/admin/commission-summary/",
				{ throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

/** Every user's payments; unlike `usePayments` it is not scoped to the caller. */
const useGetAllPayments: CustomQueryHook<
	getAllPaymentsOutput,
	getAllPaymentsInput
> = (payload, options) => {
	return useQuery({
		queryKey: ADMIN_SERVICE_KEYS.useGetAllPayments(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/marketplace/payments/",
				{ params: { query: payload?.query }, throwOnError: true },
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

export {
	ADMIN_USERS_FACET_SOURCE,
	useGetAllPayments,
	useGetAdminDashboard,
	useGetTickets,
	useGetTicketsCount,
	useUpdateTicket,
	useGetSenderOwners,
	useGetCommissionSummary,
	useGetAdminUsers,
	useCreateAdminUser,
	useUpdateAdminUser,
	useAdminResetPassword,
	useActionOnOrder,
	useGetStSetting,
	useUpdateStSetting,
};
